import { useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileUp, Loader2, CheckCircle2, Download } from "lucide-react";
import { ExpiryField } from "@/components/knowledge/Expiry";
import { V } from "@/lib/vocab";

export const ImportQA = ({ tenantId, readOnly, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [pairs, setPairs] = useState(null); // [{title, answer, checked}]
  const [filename, setFilename] = useState("");
  const [expiry, setExpiry] = useState({ expires_at: "", expiry_action: "archive" });
  const fileRef = useRef(null);

  const reset = () => { setPairs(null); setFilename(""); setBusy(false); setCommitting(false); setExpiry({ expires_at: "", expiry_action: "archive" }); };

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/tenants/${tenantId}/knowledge/import-qa/extract`, fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      setPairs(r.data.pairs.map((p) => ({ ...p, checked: true })));
      setFilename(r.data.filename || f.name);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't extract Q&A from that file");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const upd = (i, field, v) => setPairs((ps) => ps.map((p, j) => (j === i ? { ...p, [field]: v } : p)));
  const nChecked = (pairs || []).filter((p) => p.checked).length;

  const commit = async () => {
    setCommitting(true);
    try {
      const r = await api.post(`/tenants/${tenantId}/knowledge/import-qa/commit`,
        { pairs: pairs.filter((p) => p.checked).map(({ title, answer }) => ({ title, answer })),
          expires_at: expiry.expires_at || null, expiry_action: expiry.expiry_action || "archive" });
      toast.success(`${r.data.created} Q&A pair(s) added — your concierge knows them now`);
      setOpen(false); reset(); onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally { setCommitting(false); }
  };

  return (
    <>
      <button data-testid="import-qa-btn" disabled={readOnly} onClick={() => { reset(); setOpen(true); }}
        className="inline-flex items-center gap-2 rounded-full border border-sand-300 px-4 py-2 text-sm font-semibold text-ink hover:border-redline-300 hover:bg-redline-50/40 transition-colors disabled:opacity-50">
        <FileUp className="h-4 w-4" strokeWidth={1.75} /> Import Q&A
      </button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="import-qa-dialog">
          <DialogHeader>
            <DialogTitle>Import questions you've already answered</DialogTitle>
            <DialogDescription>
              Got a FAQ, a spreadsheet of {V.lc("person")} questions and your answers, a Word doc, or a helpdesk export?
              Upload it as-is — no special format needed. The AI pulls out each question-and-answer pair, you review
              and edit, and every pair you keep becomes a quick fact your concierge answers from.
            </DialogDescription>
          </DialogHeader>

          {!pairs && (
            <div className="rounded-xl border border-sand-200 bg-sand-50/60 px-3.5 py-3 text-xs text-ink/70 space-y-1.5" data-testid="import-qa-help">
              <div className="font-bold text-ink text-[11px] uppercase tracking-wide">What works</div>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>A spreadsheet or CSV with a <b>Question</b> column and an <b>Answer</b> column</li>
                <li>A Word doc or PDF FAQ ("Q: … A: …", numbered lists, headings with answers under them)</li>
                <li>Exports from TownSq, Vantaca, AppFolio, Zendesk, or a pasted email thread saved as .txt</li>
              </ul>
              <a href="/templates/ask-redline-qa-template.csv" download data-testid="import-qa-template"
                className="inline-flex items-center gap-1.5 font-bold text-redline-600 hover:text-redline-700 pt-0.5">
                <Download className="h-3.5 w-3.5" /> Download the CSV template
              </a>
            </div>
          )}

          {!pairs && (
            <label data-testid="import-qa-dropzone"
              className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-sand-300 py-10 cursor-pointer hover:border-redline-300 hover:bg-redline-50/30 transition-colors">
              {busy ? (
                <><Loader2 className="h-6 w-6 animate-spin text-redline-500" /><span className="text-sm text-muted-foreground">Reading file and extracting Q&A…</span></>
              ) : (
                <><FileUp className="h-6 w-6 text-redline-500" strokeWidth={1.75} />
                  <span className="text-sm font-semibold">Choose a file</span>
                  <span className="text-xs text-muted-foreground">CSV, PDF, DOC/DOCX, XLSX or TXT — up to 15 MB</span></>
              )}
              <input ref={fileRef} type="file" accept=".csv,.pdf,.doc,.docx,.xlsx,.txt" className="hidden" onChange={pick} disabled={busy} />
            </label>
          )}

          {pairs && (
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground">Found <b>{pairs.length}</b> pair(s) in <b>{filename}</b> — uncheck anything wrong, edit freely.</div>
              <div className="space-y-2">
                {pairs.map((p, i) => (
                  <div key={i} data-testid={`import-qa-pair-${i}`}
                    className={`rounded-xl border p-3 ${p.checked ? "border-sand-200 bg-white" : "border-sand-100 bg-sand-50/60 opacity-60"}`}>
                    <div className="flex items-start gap-2">
                      <input type="checkbox" checked={p.checked} onChange={(e) => upd(i, "checked", e.target.checked)} className="mt-1.5 accent-[#c62828]" />
                      <div className="flex-1 space-y-1.5">
                        <input value={p.title} onChange={(e) => upd(i, "title", e.target.value)}
                          className="w-full text-sm font-semibold bg-transparent border-b border-transparent focus:border-sand-300 focus:outline-none" />
                        <textarea value={p.answer} onChange={(e) => upd(i, "answer", e.target.value)} rows={2}
                          className="w-full text-sm text-muted-foreground bg-transparent rounded-lg border border-sand-100 focus:border-sand-300 focus:outline-none p-2" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="rounded-xl border border-sand-200 bg-sand-50/60 px-3.5 py-3">
                <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-1.5">Expires <span className="normal-case tracking-normal font-normal">(optional · applies to every pair you import)</span></div>
                <ExpiryField compact testid="import-qa-expiry" value={expiry.expires_at} action={expiry.expiry_action}
                  onChange={(p) => setExpiry({ expires_at: p.expires_at || "", expiry_action: p.expiry_action || "archive" })} />
              </div>
              <div className="flex items-center justify-between pt-1">
                <button onClick={reset} className="text-sm text-muted-foreground hover:text-ink">Start over</button>
                <button data-testid="import-qa-commit" onClick={commit} disabled={committing || nChecked === 0}
                  className="rounded-full bg-redline-500 text-white font-semibold px-6 py-2.5 flex items-center gap-2 hover:bg-redline-600 transition-colors disabled:opacity-50">
                  {committing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" strokeWidth={1.75} />}
                  Add {nChecked} to knowledge
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
